import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  Check,
  ChevronDown,
  CircleHelp,
  Cloud,
  LogOut,
  Moon,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Sun,
  UserRound,
} from "lucide-react";
import { usePortal } from "../state/PortalContext";
import { hasPaperKingdomProductAccess } from "../domain/access";
import { useTheme } from "../state/useTheme";

export function Brand() {
  return (
    <span className="brand">
      <img
        className="brand__image"
        src="/paperkingdom/Logo_white.png"
        alt="Paper Kingdom Publishing Studio"
      />
    </span>
  );
}

export function AppSidebarPortal({ children }: { children: ReactNode }) {
  const [sidebarRoot, setSidebarRoot] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    setSidebarRoot(document.getElementById("app-sidebar-context"));
    return () => setSidebarRoot(null);
  }, []);

  return sidebarRoot ? createPortal(children, sidebarRoot) : null;
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    session,
    authStatus,
    passwordRecovery,
    storageMode,
    canAdmin,
    saveStatus,
    entitlements,
    signOut,
    setPreviewRole,
  } = usePortal();
  const { theme, toggleTheme } = useTheme();
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const hasPaperKingdomAccess = hasPaperKingdomProductAccess(
    entitlements,
    canAdmin,
  );

  useEffect(() => {
    if (authStatus !== "signed-out") return;
    void navigate({ to: "/login", replace: true });
  }, [authStatus, navigate]);

  useEffect(() => {
    if (authStatus !== "ready" || !session || hasPaperKingdomAccess) return;
    void navigate({ to: "/account", replace: true });
  }, [authStatus, hasPaperKingdomAccess, navigate, session]);

  useEffect(() => {
    if (!passwordRecovery) return;
    void navigate({ to: "/reset-password", replace: true });
  }, [navigate, passwordRecovery]);

  useEffect(() => {
    document.title = "Paper Kingdom Studio";
  }, [location.pathname]);

  useEffect(() => {
    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "noindex,nofollow";
  }, []);

  useEffect(() => {
    const closeAccountMenu = (event: PointerEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setAccountOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeAccountMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeAccountMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const openAdminAccess = () => {
    if (!canAdmin) return;
    setAccountOpen(false);
    void navigate({ to: "/admin" });
  };

  if (authStatus === "loading") {
    return (
      <main
        className="auth-page auth-page--boot"
        aria-busy="true"
        aria-label="Opening Paper Kingdom"
      />
    );
  }

  if (
    authStatus === "signed-out" ||
    !session ||
    passwordRecovery ||
    !hasPaperKingdomAccess
  ) {
    return (
      <main
        className="auth-page auth-page--boot"
        aria-busy="true"
        aria-label="Checking Paper Kingdom access"
      />
    );
  }

  return (
    <div className="app-shell">
      <div className="app-layout">
        <aside className="app-sidebar">
        <Link to="/PaperKingdom/studio" className="brand-link app-sidebar__brand">
          <Brand />
        </Link>
        <nav className="site-nav app-sidebar__nav" aria-label="Main navigation">
          <Link to="/PaperKingdom/studio" activeProps={{ className: "is-active" }}>
            <Sparkles aria-hidden="true" size={17} />
            Studio
          </Link>
          <Link to="/PaperKingdom/help" activeProps={{ className: "is-active" }}>
            <CircleHelp aria-hidden="true" size={17} />
            Help
          </Link>
        </nav>

        {session?.role === "admin" ? (
          <div className="app-sidebar__admin" role="status">
            <div>
              <ShieldCheck size={16} />
              <span>
                <strong>Administrator</strong>
                <small>Paper Kingdom access controls</small>
              </span>
            </div>
            <button onClick={openAdminAccess}>Manage access</button>
            <button onClick={() => setPreviewRole("buyer")}>Publisher view</button>
          </div>
        ) : null}

        <div id="app-sidebar-context" className="app-sidebar__context" />

        <div className="app-sidebar__footer">
          <span
            className={`save-state save-state--${saveStatus}`}
            role="status"
            aria-live="polite"
          >
            {storageMode === "cloud" ? (
              <>
                <Cloud size={11} aria-hidden="true" />
                {saveStatus === "saving"
                  ? "Saving to cloud…"
                  : saveStatus === "error"
                    ? "Cloud save failed"
                    : saveStatus === "saved"
                      ? "Saved to cloud"
                      : "Cloud workspace"}
              </>
            ) : saveStatus === "saving"
              ? "Saving…"
              : saveStatus === "error"
                ? "Save failed"
                : saveStatus === "saved"
                  ? "Saved on this device"
                  : "Local workspace"}
          </span>
          <div className="account-menu" ref={accountRef}>
            <button
              className="account-trigger"
              type="button"
              aria-haspopup="menu"
              aria-expanded={accountOpen}
              onClick={() => setAccountOpen((open) => !open)}
            >
              <span className="account-trigger__avatar">
                {session?.role === "admin" ? <ShieldCheck size={16} /> : "P"}
              </span>
              <span className="account-trigger__label">
                <strong>{session?.role === "admin" ? "Administrator" : "Publisher"}</strong>
                <small>Paper Kingdom account</small>
              </span>
              <ChevronDown aria-hidden="true" size={15} />
            </button>
            {accountOpen ? (
              <div className="account-popover" role="menu">
                <div className="account-popover__identity">
                  <span>P</span>
                  <div>
                    <strong>Paper Kingdom account</strong>
                    <small>Signed-in member</small>
                  </div>
                </div>
                <button
                  role="menuitem"
                  onClick={() => {
                    setPreviewRole("buyer");
                    setAccountOpen(false);
                  }}
                >
                  <UserRound size={17} />
                  <span>
                    <strong>Publisher view</strong>
                    <small>Browse exactly what this account owns</small>
                  </span>
                  {session?.role === "buyer" ? <Check size={16} /> : null}
                </button>
                {canAdmin ? (
                  <button role="menuitem" onClick={openAdminAccess}>
                    <SlidersHorizontal size={17} />
                    <span>
                      <strong>Admin access</strong>
                      <small>Manage product access</small>
                    </span>
                  </button>
                ) : null}
                <button
                  role="menuitem"
                  onClick={() => {
                    toggleTheme();
                    setAccountOpen(false);
                  }}
                >
                  {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
                  <span>
                    <strong>{theme === "dark" ? "Light mode" : "Dark mode"}</strong>
                    <small>
                      {theme === "dark"
                        ? "Use the light appearance"
                        : "Use the dark appearance"}
                    </small>
                  </span>
                </button>
                <button
                  role="menuitem"
                  onClick={() => {
                    setAccountOpen(false);
                    void signOut();
                  }}
                >
                  <LogOut size={17} />
                  <span>
                    <strong>Sign out</strong>
                    <small>End this secure session</small>
                  </span>
                </button>
                <p>
                  {storageMode === "cloud"
                    ? "Account access and projects are synced through AIAgentOffer."
                    : "Projects are cached on this device while cloud sync is unavailable."}
                </p>
              </div>
            ) : null}
          </div>
        </div>
        </aside>

        <div className="app-content">
          <Outlet />
        </div>
      </div>

    </div>
  );
}
